import { Change, diffChars, diffWordsWithSpace } from 'diff';

export type InlineDiffSegmentType = 'equal' | 'added' | 'removed';

export interface InlineDiffSegment {
  value: string;
  type: InlineDiffSegmentType;
}

const MIN_CHAR_REFINEMENT_RATIO = 0.3;

const changeType = (change: Change): InlineDiffSegmentType => {
  if (change.added) {
    return 'added';
  }
  if (change.removed) {
    return 'removed';
  }
  return 'equal';
};

const toSegment = (change: Change): InlineDiffSegment => ({
  value: change.value,
  type: changeType(change),
});

const commonRatio = (changes: Change[], oldValue: string, newValue: string) => {
  const equalLength = changes
    .filter((change) => !change.added && !change.removed)
    .reduce((sum, change) => sum + change.value.length, 0);

  return equalLength / Math.max(oldValue.length, newValue.length, 1);
};

const asReplacement = (oldValue: string, newValue: string): Change[] => [
  { value: oldValue, added: false, removed: true, count: oldValue.length },
  { value: newValue, added: true, removed: false, count: newValue.length },
];

const refineReplacement = (oldValue: string, newValue: string): Change[] => {
  const charChanges = diffChars(oldValue, newValue);
  if (
    !charChanges ||
    commonRatio(charChanges, oldValue, newValue) < MIN_CHAR_REFINEMENT_RATIO
  ) {
    return asReplacement(oldValue, newValue);
  }
  return charChanges;
};

const mergeSegments = (segments: InlineDiffSegment[]): InlineDiffSegment[] => {
  const merged: InlineDiffSegment[] = [];

  for (const segment of segments) {
    if (!segment.value) {
      continue;
    }

    const last = merged[merged.length - 1];
    if (last && last.type === segment.type) {
      last.value += segment.value;
    } else {
      merged.push({ ...segment });
    }
  }

  return merged;
};

export const getInlineDiff = (
  oldValue: string,
  newValue: string,
): InlineDiffSegment[] => {
  if (oldValue === newValue) {
    return oldValue ? [{ value: oldValue, type: 'equal' }] : [];
  }
  if (!oldValue) {
    return [{ value: newValue, type: 'added' }];
  }
  if (!newValue) {
    return [{ value: oldValue, type: 'removed' }];
  }

  const wordChanges =
    diffWordsWithSpace(oldValue, newValue) ?? asReplacement(oldValue, newValue);
  const refined: Change[] = [];

  for (let i = 0; i < wordChanges.length; i += 1) {
    const current = wordChanges[i];
    const next = wordChanges[i + 1];

    if (current.removed && next?.added) {
      refined.push(...refineReplacement(current.value, next.value));
      i += 1;
    } else {
      refined.push(current);
    }
  }

  return mergeSegments(refined.map(toSegment));
};

export const segmentsForSide = (
  segments: InlineDiffSegment[],
  side: 'old' | 'new',
): InlineDiffSegment[] =>
  segments.filter((segment) => {
    if (segment.type === 'equal') {
      return true;
    }
    return side === 'old'
      ? segment.type === 'removed'
      : segment.type === 'added';
  });
